'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import { LuPlus } from 'react-icons/lu';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Heading } from '../ui/heading';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export function HeaderMenu({
  categories,
  whiteText,
}: {
  categories: CategoriesEntity[];
  whiteText: boolean;
}) {
  const [activeCategory, setActiveCategory] = useState<CategoriesEntity | null>(null);
  const [displayedCategory, setDisplayedCategory] = useState<CategoriesEntity | null>(null);
  const [navigatingSlug, setNavigatingSlug] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  const isOpen = activeCategory !== null;

  const closeDropdown = useCallback(() => {
    setActiveCategory(null);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setDisplayedCategory(null), 220);
  }, []);

  const openCategory = useCallback((category: CategoriesEntity) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDisplayedCategory(category);
    setActiveCategory(category);
  }, []);

  const handleCategoryClick = (category: CategoriesEntity) => {
    if (activeCategory?.id === category.id) {
      closeDropdown();
    } else {
      openCategory(category);
    }
  };

  useEffect(() => {
    closeDropdown();
    setNavigatingSlug(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeDropdown]);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  return (
    /**
     * Figma puts the category nav inline in the header row (not on its own line below it), at
     * Montserrat Medium 18px with a 24px gap and a 14px "+" affordance per item.
     */
    <div
      ref={containerRef}
      className={cn(
        'hidden items-center justify-center gap-4 lg:flex lg:gap-5 xl:gap-6',
        'ea-header-menu',
      )}
    >
      {categories.map(category => {
        const hasChilds = category?.childs && category.childs.length > 0;
        const labelClass = cn(
          'flex items-center justify-center gap-1 whitespace-nowrap text-[15px] font-medium leading-[1.3] transition-opacity hover:opacity-75 lg:text-[16px] xl:text-[18px]',
          whiteText ? 'text-white' : 'text-black',
        );

        if (!hasChilds) {
          return (
            <Link
              key={category.name}
              href={ROUTES.CATEGORY({ slug: category.slug })}
              prefetchOnHover
              className={labelClass}
            >
              {category.name}
            </Link>
          );
        }
        return (
          <button
            key={category.name}
            onClick={() => handleCategoryClick(category)}
            className={labelClass}
          >
            {category.name}
            <LuPlus
              size={14}
              className={cn(
                'shrink-0 transition-transform duration-200',
                whiteText ? 'text-white' : 'text-black',
                activeCategory?.id === category.id ? 'rotate-45' : 'rotate-0',
              )}
            />
          </button>
        );
      })}

      {/* The nav is now only as wide as its items, so the panel is centred on it and capped to
          the page container instead of inheriting a full-width parent. */}
      <div
        className={cn(
          'absolute left-1/2 top-full z-40 w-[min(1400px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-[4px] bg-white shadow-lg',
          'mt-3 transition-all duration-200 ease-out',
          isOpen
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-1.5 opacity-0',
        )}
      >
        {displayedCategory && (
          <div className="relative flex w-full items-start justify-between gap-6 p-6">
            <div className="flex w-full flex-col items-start justify-start gap-6">
              <Link href={displayedCategory?.slug} prefetchOnHover className="generic-hover">
                <Heading title={displayedCategory.name} />
              </Link>
              <div className="grid w-full grid-cols-3 gap-3">
                {displayedCategory.childs?.map(child => (
                  <CategoryCard
                    key={child.name}
                    child={child}
                    isNavigating={navigatingSlug === child.slug}
                    onNavigate={() => setNavigatingSlug(child.slug)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryCard({
  child,
  onNavigate,
  isNavigating,
}: {
  child: CategoriesEntity;
  onNavigate: () => void;
  isNavigating: boolean;
}) {
  return (
    <Link
      href={child.slug}
      prefetchOnHover
      onClick={onNavigate}
      className={cn(
        'relative flex items-center justify-start gap-3 overflow-hidden rounded-[4px] border border-border p-4 transition-all hover:border-primary',
        isNavigating && 'pointer-events-none',
      )}
    >
      {child?.icon?.url && (
        <Image
          src={child?.icon?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(child?.icon, child?.name)}
          width={48}
          height={48}
          className="max-h-12 min-h-12 min-w-12 max-w-12 shrink-0 rounded-[3px] bg-[#F4F4F4] object-contain p-1"
          sizes="48px"
        />
      )}

      <Heading tag="h5" title={child.name} className={{ title: 'pr-4 text-black' }} />
      <div className="absolute z-10 flex h-full w-full items-center justify-end p-4">
        <Spinner className={cn(isNavigating ? 'opacity-100' : 'opacity-0', 'mr-4')} />
      </div>
    </Link>
  );
}
